import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  type User,
} from 'firebase/auth';
import { Timestamp, doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db, firebaseAuth, githubProvider, googleProvider } from '@/firebase';
import type { UserProfile } from '@/types';

export async function loginWithGoogle(): Promise<User> {
  const credential = await signInWithPopup(firebaseAuth(), googleProvider);
  await ensureUserDoc(credential.user);
  return credential.user;
}

export async function loginWithGithub(): Promise<User> {
  const credential = await signInWithPopup(firebaseAuth(), githubProvider);
  await ensureUserDoc(credential.user);
  return credential.user;
}

export async function registerWithEmail(name: string, email: string, password: string): Promise<User> {
  const credential = await createUserWithEmailAndPassword(firebaseAuth(), email, password);
  await updateProfile(credential.user, { displayName: name });
  await ensureUserDoc(credential.user, name);
  return credential.user;
}

export async function loginWithEmail(email: string, password: string): Promise<User> {
  const credential = await signInWithEmailAndPassword(firebaseAuth(), email, password);
  return credential.user;
}

export async function resetPassword(email: string): Promise<void> {
  await sendPasswordResetEmail(firebaseAuth(), email);
}

export async function logout(): Promise<void> {
  await signOut(firebaseAuth());
}

/**
 * First-login fallback: the onUserCreated Cloud Function normally creates the
 * profile, but writing it client-side too makes local/emulator setups work
 * even before functions are deployed. Rules restrict the allowed shape.
 */
export async function ensureUserDoc(user: User, nameOverride?: string): Promise<void> {
  const ref = doc(db(), 'users', user.uid);
  const snapshot = await getDoc(ref);
  if (snapshot.exists()) return;
  await setDoc(ref, {
    uid: user.uid,
    name: nameOverride ?? user.displayName ?? user.email?.split('@')[0] ?? 'User',
    email: user.email ?? '',
    photoURL: user.photoURL ?? null,
    role: 'user',
    totalDownloads: 0,
    downloadsToday: 0,
    storageUsedBytes: 0,
    platformCounts: {},
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export function mapUserProfile(data: Record<string, unknown>): UserProfile {
  return {
    uid: String(data.uid ?? ''),
    name: String(data.name ?? ''),
    email: String(data.email ?? ''),
    photoURL: (data.photoURL as string | null) ?? null,
    role: data.role === 'admin' ? 'admin' : 'user',
    totalDownloads: Number(data.totalDownloads ?? 0),
    downloadsToday: Number(data.downloadsToday ?? 0),
    storageUsedBytes: Number(data.storageUsedBytes ?? 0),
    platformCounts: (data.platformCounts as UserProfile['platformCounts']) ?? {},
    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toMillis() : Date.now(),
  };
}

export function authErrorMessage(error: unknown): string {
  const code = (error as { code?: string }).code ?? '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Invalid email or password.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/invalid-email':
      return 'Enter a valid email address.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'The sign-in window was closed before finishing.';
    case 'auth/account-exists-with-different-credential':
      return 'This email is already linked to a different sign-in method.';
    case 'auth/too-many-requests':
      return 'Too many attempts — please wait a moment and try again.';
    default:
      return error instanceof Error ? error.message : 'Something went wrong. Please try again.';
  }
}
